import { useState, useRef } from "react"
import { Upload, CheckCircle, XCircle } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Progress } from "@/components/ui/progress"

const ACCEPTED_FORMATS = ".mp3,.wav,.m4a,.ogg,.webm,.flac,.aac"

interface AudioUploadScreenProps {
  onComplete: (data: { fileName: string }) => void
  onCancel: () => void
}

export function AudioUploadScreen({ onComplete, onCancel }: AudioUploadScreenProps) {
  const [file, setFile] = useState<File | null>(null)
  const [progress, setProgress] = useState(0)
  const [uploading, setUploading] = useState(false)
  const [uploaded, setUploaded] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const selected = e.target.files?.[0]
    if (selected) {
      setFile(selected)
      setError(null)
    }
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    const dropped = e.dataTransfer.files[0]
    if (dropped) {
      setFile(dropped)
      setError(null)
    }
  }

  async function handleUpload() {
    if (!file) return

    setUploading(true)
    setProgress(0)

    const formData = new FormData()
    formData.append("file", file)
    formData.append("file_name", file.name.replace(/\.[^/.]+$/, ""))

    try {
      const xhr = new XMLHttpRequest()

      xhr.upload.addEventListener("progress", (e) => {
        if (e.lengthComputable) {
          setProgress(Math.round((e.loaded / e.total) * 100))
        }
      })

      await new Promise<void>((resolve, reject) => {
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve()
          } else {
            reject(new Error("Upload failed"))
          }
        }
        xhr.onerror = () => reject(new Error("Upload failed"))
        xhr.open("POST", "/api/audio_uploads")
        const meta = document.querySelector('meta[name="csrf-token"]')
        xhr.setRequestHeader("X-CSRF-Token", meta?.getAttribute("content") || "")
        xhr.send(formData)
      })

      setUploaded(true)
      onComplete({ fileName: file.name })
    } catch {
      setError("Upload failed. Please try again.")
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="flex flex-col items-center justify-center h-full gap-6 p-8" data-testid="audio-upload-screen">
      {!file && (
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          className="flex w-full max-w-sm cursor-pointer flex-col items-center gap-4 rounded-xl border-2 border-dashed border-muted-foreground/25 p-12 transition-colors hover:border-primary/50"
          data-testid="drop-zone"
        >
          <Upload className="h-12 w-12 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Drop audio file here or click to browse</p>
          <p className="text-xs text-muted-foreground/60">MP3, WAV, M4A, OGG, WebM, FLAC, AAC</p>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED_FORMATS}
            onChange={handleFileSelect}
            className="hidden"
            data-testid="file-input"
          />
        </div>
      )}

      {file && !uploaded && (
        <div className="w-full max-w-sm space-y-4">
          <div className="flex items-center gap-3 rounded-lg border p-4">
            <Upload className="h-5 w-5 text-muted-foreground" />
            <div className="flex-1 min-w-0">
              <p className="truncate text-sm font-medium">{file.name}</p>
              <p className="text-xs text-muted-foreground">
                {(file.size / (1024 * 1024)).toFixed(1)} MB
              </p>
            </div>
          </div>

          {uploading && (
            <Progress value={progress} className="h-2" data-testid="upload-progress" />
          )}

          {error && (
            <div className="flex items-center gap-2 text-sm text-destructive">
              <XCircle className="h-4 w-4" />
              {error}
            </div>
          )}

          <div className="flex gap-3">
            <Button variant="outline" onClick={onCancel} disabled={uploading}>
              Cancel
            </Button>
            <Button onClick={handleUpload} disabled={uploading} className="flex-1">
              {uploading ? `Uploading ${progress}%` : "Upload & Transcribe"}
            </Button>
          </div>
        </div>
      )}

      {uploaded && (
        <div className="flex flex-col items-center gap-4" data-testid="upload-complete">
          <CheckCircle className="h-16 w-16 text-green-500" />
          <p className="text-lg font-medium">Upload complete</p>
          <p className="text-sm text-muted-foreground">Transcription is processing...</p>
        </div>
      )}
    </div>
  )
}
